import { FormArray, FormGroup } from '@angular/forms';
import { ProdutoListStruct } from '../../../../../shared/services/structs/produto/produto-list.struct';
import { UtilService } from '../../../../../shared/services/util.service';
import { ContextoDePreco, percentualDoPreco, precoDoPercentual } from './venda-item.form';
import { resolverPrecoDoProduto } from './venda-item.preco';
import { totalDoItem } from './venda-item.calculo';

/**
 * Kit (§6.9): o plano de lançamento e as contas do agrupador.
 *
 * A conta do kit de PREÇO FIXO é a MESMA do cadastro de produto (`produto-kits.component.ts`,
 * `getRateioKit` / `getUnitarioNoKit`): o preço do kit dividido pela soma dos componentes é o
 * FATOR, e o preço de cada componente no kit é o preço base × fator, nas casas de preço da
 * empresa. O que vale é o que fica nos filhos — o total do kit na lista é a soma dos totais deles,
 * exatamente como a folha recalcula (`valorTotal = qtd × unit`, 2 casas). Kit por SOMA não tem
 * preço: cada componente entra com o próprio preço de lista.
 *
 * Precisão: preço nas casas de preço, quantidade nas casas de quantidade (as duas do contexto,
 * vindas do usuário logado), total em 2 casas — as mesmas do `NormalizarCasasDecimaisDosItens`.
 * Não se muda a precisão configurada para fechar centavo (Pedro, 24/09).
 */

const CASAS_DINHEIRO = 2;

export interface PlanoDoKit {
    produto: ProdutoListStruct;
    isPrecoFixo: boolean;
    quantidadeKits: number;
    /** Preço de lista do kit (fixo: a lista do produto-kit; soma: Σ dos componentes). */
    precoKitLista: number;
    /** Preço negociado do kit: lista × (1 − desconto). */
    precoKit: number;
    percentualDescontoKit: number;
    idListaPreco: number | null;
    componentes: ComponenteDoPlano[];
    /**
     * Kit FIXO: a soma dos totais dos componentes fechou exatamente em `precoKit × quantidadeKits`
     * (Pedro, 24/09: a sobra da dízima vai para o último componente). Falso só quando as casas de
     * preço não permitem — e aí o kit não é lançado. Kit por soma é sempre verdadeiro.
     */
    fechouNoPreco: boolean;
}

/**
 * Fecha Σ totais dos componentes no alvo (preço do kit × kits) ajustando UNITÁRIOS dentro das casas
 * de preço — a sobra (positiva ou negativa) vai para o ÚLTIMO componente; se o passo da precisão não
 * alcança o centavo nele (quantidade grande), o anterior absorve, e assim por diante. O total de cada
 * componente continua `qtd × unitário` em 2 casas, que é o que a folha recalcula e grava.
 */
export function fecharNoPrecoDoKit(
    quantidades: number[],
    unitarios: number[],
    alvo: number,
    casasPreco: number,
): { unitarios: number[]; totais: number[]; fechou: boolean } {
    const un = [...unitarios];
    const totais = quantidades.map((q, i) => totalDoItem(q, un[i]));
    const dois = (v: number): number => UtilService.arredondarAbnt(v, CASAS_DINHEIRO);
    const preco = (v: number): number => UtilService.arredondarAbnt(v, casasPreco);
    const passo = Math.pow(10, -casasPreco);
    let residual = dois(alvo - totais.reduce((a, t) => a + t, 0));

    for (let i = quantidades.length - 1; i >= 0 && residual !== 0; i--) {
        const q = quantidades[i];

        if (q <= 0) continue;

        const alvoDoItem = dois(totais[i] + residual);
        const base = preco(alvoDoItem / q);
        let exato: number | null = null;

        // Procura, em passos da precisão ao redor da divisão, o unitário cujo total bate o alvo.
        for (let k = 0; k <= 50 && exato === null; k++) {
            for (const sinal of k === 0 ? [0] : [1, -1]) {
                const candidato = preco(base + sinal * k * passo);

                if (candidato >= 0 && totalDoItem(q, candidato) === alvoDoItem) {
                    exato = candidato;
                    break;
                }
            }
        }

        if (exato !== null) {
            un[i] = exato;
            totais[i] = alvoDoItem;
            residual = 0;
            break;
        }

        // Não alcança o centavo neste componente: absorve o que der na direção certa e passa a sobra
        // para o anterior.
        const totalAproximado = totalDoItem(q, base);
        const ganho = dois(totalAproximado - totais[i]);

        if (ganho !== 0 && Math.sign(ganho) === Math.sign(residual) && Math.abs(ganho) <= Math.abs(residual)) {
            un[i] = base;
            totais[i] = totalAproximado;
            residual = dois(residual - ganho);
        }
    }

    return { unitarios: un, totais, fechou: residual === 0 };
}

export interface ComponenteDoPlano {
    produto: ProdutoListStruct;
    /** Quantidade do componente em UM kit (composição). */
    quantidadeComposicao: number;
    /** `quantidadeComposicao × quantidadeKits`, nas casas de quantidade. */
    quantidade: number;
    /** Preço de lista do componente fora do kit (referência do fator). */
    precoBase: number;
    /** Preço de lista do componente NO kit: fixo → `precoBase × fator`; soma → `precoBase`. */
    precoBrutoUnitario: number;
    /** `precoBrutoUnitario × (1 − desconto do kit)`, nas casas de preço. */
    precoLiquidoUnitario: number;
    /** `quantidade × precoLiquidoUnitario`, 2 casas. */
    valorTotal: number;
}

interface BaseDoComponente {
    produto: ProdutoListStruct;
    quantidadeComposicao: number;
    precoBase: number;
}

/**
 * A MESMA regra do item avulso (`preencherDoProduto`, §6.2.2.3): sem lista vigente o componente
 * entra a ZERO, e não com o `produto.preco` da lista padrão por baixo do pano — senão o mesmo
 * produto tinha um preço avulso e outro dentro do kit (Pedro, 25/09: consistência com a story).
 */
function precoBaseDoComponente(produto: ProdutoListStruct, contexto: ContextoDePreco): number {
    const resolvido = resolverPrecoDoProduto(produto.listasPreco, contexto.idListaCabecalho, contexto.priorizarMenorPreco);

    return Number(resolvido.lista?.preco ?? 0);
}

/** Σ `precoBase × quantidadeComposicao`, sem arredondar — a referência do fator (cadastro: `getSomaBaseKit`). */
function somaBaseDoKit(bases: { precoBase: number; quantidadeComposicao: number }[]): number {
    return bases.reduce((a, b) => a + b.precoBase * b.quantidadeComposicao, 0);
}

/** Fator do kit fixo (cadastro: `getRateioKit`): preço do kit ÷ soma base; 1 quando não há base. */
function fatorDoKit(precoKitLista: number, somaBase: number): number {
    return somaBase > 0 ? precoKitLista / somaBase : 1;
}

/** Preço de lista do componente NO kit, nas casas de preço (cadastro: `getUnitarioNoKit`, exibido pelo `abnt`). */
function precoListaNoKit(precoBase: number, fator: number, casasPreco: number): number {
    return UtilService.arredondarAbnt(precoBase * fator, casasPreco);
}

function quantidadeDoComponente(quantidadeComposicao: number, kits: number, casasQuantidade: number): number {
    return UtilService.arredondarAbnt(quantidadeComposicao * kits, casasQuantidade);
}

/**
 * Resolve lista e desconto do kit a partir do que o usuário informou. Preço informado acima da
 * lista vira a nova lista (acréscimo não é desconto); abaixo vira desconto do kit
 * (`percentualDoPreco`).
 */
function resolverPrecoEDesconto(
    precoKitLista: number,
    precoKitInformado: number | null,
    percentualDescontoKit: number,
): { precoKitLista: number; desconto: number } {
    let lista = precoKitLista;
    let desconto = Math.min(100, Math.max(0, Number(percentualDescontoKit) || 0));

    if (precoKitInformado != null) {
        const informado = Math.max(0, Number(precoKitInformado) || 0);

        if (lista <= 0 || informado > lista) {
            lista = informado;
            desconto = 0;
        } else {
            desconto = percentualDoPreco(lista, informado);
        }
    }

    return { precoKitLista: lista, desconto };
}

export function montarPlanoDoKit(
    produto: ProdutoListStruct,
    contexto: ContextoDePreco,
    quantidadeKits: number,
    precoKitInformado: number | null,
    percentualDescontoKit: number,
): PlanoDoKit {
    const composicao = produto.composicaoKit ?? [];
    const kits = Math.max(0, Number(quantidadeKits) || 0);
    const isPrecoFixo = !!produto.isPrecoFixoKit;

    const bases: BaseDoComponente[] = composicao.map((c) => ({
        produto: c.produtoListStruct as ProdutoListStruct,
        quantidadeComposicao: Number(c.quantidade) || 0,
        precoBase: c.produtoListStruct ? precoBaseDoComponente(c.produtoListStruct, contexto) : 0,
    }));

    const somaBase = somaBaseDoKit(bases);
    const listaDoKit = isPrecoFixo
        ? resolverPrecoDoProduto(produto.listasPreco, contexto.idListaCabecalho, contexto.priorizarMenorPreco)
        : null;

    const resolvido = resolverPrecoEDesconto(
        isPrecoFixo && listaDoKit?.lista
            ? Number(listaDoKit.lista.preco)
            : UtilService.arredondarAbnt(somaBase, contexto.casasDecimaisPreco),
        isPrecoFixo ? precoKitInformado : null,
        percentualDescontoKit,
    );

    const fator = isPrecoFixo ? fatorDoKit(resolvido.precoKitLista, somaBase) : 1;

    const precoKit = precoDoPercentual(resolvido.precoKitLista, resolvido.desconto, contexto.casasDecimaisPreco);

    const componentes: ComponenteDoPlano[] = bases.map((b) => {
        const precoBrutoUnitario = precoListaNoKit(b.precoBase, fator, contexto.casasDecimaisPreco);
        const precoLiquidoUnitario = precoDoPercentual(precoBrutoUnitario, resolvido.desconto, contexto.casasDecimaisPreco);
        const quantidade = quantidadeDoComponente(b.quantidadeComposicao, kits, contexto.casasDecimaisQuantidade);

        return {
            produto: b.produto,
            quantidadeComposicao: b.quantidadeComposicao,
            quantidade,
            precoBase: b.precoBase,
            precoBrutoUnitario,
            precoLiquidoUnitario,
            valorTotal: totalDoItem(quantidade, precoLiquidoUnitario),
        };
    });

    // Kit FIXO fecha exato no preço × kits: a sobra vai para o último componente (Pedro, 24/09).
    let fechouNoPreco = true;

    if (isPrecoFixo && kits > 0 && componentes.length) {
        const fechamento = fecharNoPrecoDoKit(
            componentes.map((c) => c.quantidade),
            componentes.map((c) => c.precoLiquidoUnitario),
            totalDoItem(kits, precoKit),
            contexto.casasDecimaisPreco,
        );

        componentes.forEach((c, i) => {
            c.precoLiquidoUnitario = fechamento.unitarios[i];
            c.valorTotal = fechamento.totais[i];
        });
        fechouNoPreco = fechamento.fechou;
    }

    return {
        produto,
        isPrecoFixo,
        quantidadeKits: kits,
        precoKitLista: resolvido.precoKitLista,
        precoKit,
        percentualDescontoKit: resolvido.desconto,
        idListaPreco: listaDoKit?.lista?.idListaPreco ?? null,
        componentes,
        fechouNoPreco,
    };
}

/** Total de UM kit no plano: Σ do total de cada componente com a quantidade da composição. */
export function totalDoPlanoPorKit(plano: PlanoDoKit): number {
    return UtilService.arredondarAbnt(
        plano.componentes.reduce((a, c) => a + totalDoItem(c.quantidadeComposicao, c.precoLiquidoUnitario), 0),
        CASAS_DINHEIRO,
    );
}

/** Total do plano inteiro (todos os kits): Σ `valorTotal` dos componentes — o que vai para a lista. */
export function totalDoPlano(plano: PlanoDoKit): number {
    return UtilService.arredondarAbnt(plano.componentes.reduce((a, c) => a + c.valorTotal, 0), CASAS_DINHEIRO);
}

/** Próximo `numeroKit` livre na lista. */
export function proximoNumeroKit(itens: FormArray): number {
    let maior = 0;

    for (const linha of itens.controls) {
        const n = Number(linha.get('kit.numeroKit')?.value ?? 0);

        if (n > maior) maior = n;
    }

    return maior + 1;
}

/**
 * Preenche a linha da lista com um componente do plano. O `descontoPercentual` gravado é o
 * DERIVADO de `precoLiquidoUnitario / precoBrutoUnitario`, e não o % digitado no kit, que pode
 * não caber nas casas de preço do componente.
 */
export function aplicarComponenteNaLinha(linha: FormGroup, plano: PlanoDoKit, componente: ComponenteDoPlano, numeroKit: number): void {
    const p = componente.produto;

    linha.get('dados')?.patchValue({
        idProduto: p.idProduto,
        descricao: p.descricao,
        codigoSku: p.codigoSku ?? null,
        gtinEan: p.gtinEan ?? null,
        idUnidadeMedida: p.idUnidadeMedida ?? null,
        siglaUnidade: p.unidade ?? null,
        isUnidadeMedidaFracionada: p.isUnidadeMedidaFracionada !== false,
        isControlaLotes: p.isControlaLotes === true,
        isControlaSeries: p.isControlaSeries === true,
        codigoNcm: p.ncm ?? null,
        pesoBruto: p.pesoBrutoKg ?? null,
        pesoLiquido: p.pesoLiquidoKg ?? null,
        quantidade: componente.quantidade,
        precoBrutoUnitario: componente.precoBrutoUnitario,
        descontoPercentual: percentualDoPreco(componente.precoBrutoUnitario, componente.precoLiquidoUnitario),
        precoLiquidoUnitario: componente.precoLiquidoUnitario,
        valorTotal: componente.valorTotal,
        idListaPreco: plano.idListaPreco,
        isListaPrecoManual: false,
    }, { emitEvent: false });

    linha.get('kit')?.patchValue({
        idProdutoKit: plano.produto.idProduto,
        numeroKit,
        quantidadeKit: plano.quantidadeKits,
        precoKit: plano.precoKit,
        percentualDescontoKit: plano.percentualDescontoKit,
        descricaoKit: plano.produto.descricao,
        codigoSkuKit: plano.produto.codigoSku ?? null,
        isPrecoFixoKit: plano.isPrecoFixo,
        quantidadeComposicao: componente.quantidadeComposicao,
        precoBaseComponente: componente.precoBase,
    }, { emitEvent: false });
}

export function linhasDoKit(itens: FormArray, numeroKit: number): FormGroup[] {
    return itens.controls
        .filter((l) => Number(l.get('kit.numeroKit')?.value) === numeroKit)
        .map((l) => l as FormGroup);
}

/** Total do grupo na lista: Σ `valorTotal` dos componentes (o que vale é o que está nos filhos). */
export function totalDoKit(linhas: FormGroup[]): number {
    return UtilService.arredondarAbnt(
        linhas.reduce((a, l) => a + Number(l.get('dados.valorTotal')?.value ?? 0), 0),
        CASAS_DINHEIRO,
    );
}

/**
 * Mudou a "Qtde de kits" no agrupador: cada componente vira `composição × kits`, nas casas de
 * quantidade. No kit FIXO os unitários voltam a `lista × (1 − desconto)` e a soma fecha de novo em
 * `preço do kit × kits` — a sobra muda com a quantidade. Devolve false quando não fechou.
 */
export function aplicarQuantidadeDeKits(
    linhas: FormGroup[],
    quantidadeKits: number,
    casasDecimaisQuantidade: number,
    casasDecimaisPreco: number,
): boolean {
    const kits = Math.max(0, Number(quantidadeKits) || 0);

    for (const linha of linhas) {
        const composicao = Number(linha.get('kit.quantidadeComposicao')?.value ?? 0);

        linha.get('kit.quantidadeKit')?.setValue(kits, { emitEvent: false });
        linha.get('dados.quantidade')?.setValue(quantidadeDoComponente(composicao, kits, casasDecimaisQuantidade), { emitEvent: false });
    }

    if (linhas[0]?.get('kit.isPrecoFixoKit')?.value !== true) {
        linhas.forEach((l) => l.get('dados.quantidade')?.updateValueAndValidity());
        return true;
    }

    return fecharLinhasNoPrecoDoKit(linhas, kits, casasDecimaisPreco);
}

/** Refaz os unitários dos componentes de um kit FIXO a partir da lista × desconto e fecha em `precoKit × kits`. */
function fecharLinhasNoPrecoDoKit(linhas: FormGroup[], kits: number, casasDecimaisPreco: number): boolean {
    const desconto = Number(linhas[0]?.get('kit.percentualDescontoKit')?.value ?? 0) || 0;
    const precoKit = Number(linhas[0]?.get('kit.precoKit')?.value ?? 0) || 0;
    const quantidades = linhas.map((l) => Number(l.get('dados.quantidade')?.value ?? 0) || 0);
    const unitarios = linhas.map((l) => precoDoPercentual(l.get('dados.precoBrutoUnitario')?.value, desconto, casasDecimaisPreco));
    const fechamento = fecharNoPrecoDoKit(quantidades, unitarios, totalDoItem(kits, precoKit), casasDecimaisPreco);

    linhas.forEach((linha, i) => {
        linha.get('dados.descontoPercentual')?.setValue(percentualDoPreco(linha.get('dados.precoBrutoUnitario')?.value, fechamento.unitarios[i]), { emitEvent: false });
        linha.get('dados.precoLiquidoUnitario')?.setValue(fechamento.unitarios[i]);
    });

    return fechamento.fechou;
}

/**
 * Mudou o "Preço do kit" no agrupador (kit FIXO): a lista do kit é a soma das listas rateadas que
 * já estão nas linhas; abaixo dela o informado vira desconto do kit, acima vira a nova lista e o
 * rateio é refeito pelo fator — a mesma conta do plano.
 */
export function aplicarPrecoDoKit(linhas: FormGroup[], precoKitInformado: number, casasDecimaisPreco: number): boolean {
    if (!linhas.length) return true;

    const bases = linhas.map((l) => ({
        precoBase: Number(l.get('kit.precoBaseComponente')?.value ?? 0),
        quantidadeComposicao: Number(l.get('kit.quantidadeComposicao')?.value ?? 0),
    }));

    const listaAtual = UtilService.arredondarAbnt(
        linhas.reduce((a, l) => a + Number(l.get('dados.precoBrutoUnitario')?.value ?? 0) * Number(l.get('kit.quantidadeComposicao')?.value ?? 0), 0),
        casasDecimaisPreco,
    );

    const resolvido = resolverPrecoEDesconto(listaAtual, precoKitInformado, 0);
    const fator = fatorDoKit(resolvido.precoKitLista, somaBaseDoKit(bases));
    const precoKit = precoDoPercentual(resolvido.precoKitLista, resolvido.desconto, casasDecimaisPreco);

    linhas.forEach((linha, i) => {
        const precoBrutoUnitario = precoListaNoKit(bases[i].precoBase, fator, casasDecimaisPreco);

        linha.get('kit')?.patchValue({ precoKit, percentualDescontoKit: resolvido.desconto }, { emitEvent: false });
        linha.get('dados')?.patchValue({ precoBrutoUnitario }, { emitEvent: false });
    });

    // Fecha em `precoKit × kits` com a sobra no último (Pedro, 24/09); é o que a folha vai conferir.
    return fecharLinhasNoPrecoDoKit(linhas, Number(linhas[0].get('kit.quantidadeKit')?.value ?? 0) || 0, casasDecimaisPreco);
}

/**
 * Kit por SOMA: o "preço do kit" é derivado — Σ `precoLiquidoUnitario × composição` —, nas casas de
 * preço, e vive no grupo para o cabeçalho e o request lerem de lá.
 */
export function sincronizarPrecoKitPorSoma(linhas: FormGroup[], casasDecimaisPreco: number): void {
    if (!linhas.length) return;

    const precoKit = UtilService.arredondarAbnt(
        linhas.reduce((a, l) => a + Number(l.get('dados.precoLiquidoUnitario')?.value ?? 0) * Number(l.get('kit.quantidadeComposicao')?.value ?? 0), 0),
        casasDecimaisPreco,
    );

    for (const linha of linhas) linha.get('kit.precoKit')?.setValue(precoKit, { emitEvent: false });
}
